/**
 * syncStore — thin pass-through layer that mirrors localStorage writes to
 * Supabase when configured. Every consumer (bookings, wallets, etc.) can call
 * `mirror(table, rows)` without breaking when Supabase is not enabled.
 *
 * When REACT_APP_SUPABASE_URL + REACT_APP_SUPABASE_ANON_KEY are set, rows are
 * upserted to the matching table. Otherwise it's a no-op.
 */
import { supabase, isSupabaseEnabled, SUPABASE_TABLES } from "@/lib/supabaseClient";

export const mirror = async (table, row) => {
    if (!isSupabaseEnabled() || !row) return { ok: false, skipped: true };
    try {
        const { error } = await supabase.from(table).upsert(row);
        if (error) return { ok: false, error: error.message };
        return { ok: true };
    } catch (err) {
        return { ok: false, error: err?.message || "Supabase error" };
    }
};

export const mirrorMany = async (table, rows) => {
    if (!isSupabaseEnabled() || !rows?.length) return { ok: false, skipped: true };
    try {
        const { error } = await supabase.from(table).upsert(rows);
        if (error) return { ok: false, error: error.message };
        return { ok: true };
    } catch (err) {
        return { ok: false, error: err?.message || "Supabase error" };
    }
};

export const remoteSelect = async (table, filter = null) => {
    if (!isSupabaseEnabled()) return { ok: false, skipped: true, data: [] };
    try {
        let q = supabase.from(table).select("*");
        if (filter) q = filter(q);
        const { data, error } = await q;
        if (error) return { ok: false, error: error.message, data: [] };
        return { ok: true, data: data || [] };
    } catch (err) {
        return { ok: false, error: err?.message || "Supabase error", data: [] };
    }
};

/**
 * One-shot read hydration on app boot. Pulls the public/published rows from
 * Supabase into the matching localStorage stores, merging by `id` so any
 * locally-created row that hasn't been synced yet is preserved.
 *
 * Safe no-op when Supabase isn't configured. Errors are swallowed —
 * localStorage remains the offline-first source of truth.
 */
const STORAGE_KEYS = {
    [SUPABASE_TABLES.SERVICES]: "neara.services",
    [SUPABASE_TABLES.EVENTS]: "neara.events",
};

const mergeById = (local, remote) => {
    const map = new Map();
    for (const r of remote || []) {
        if (r && r.id != null) map.set(String(r.id), r);
    }
    for (const r of local || []) {
        if (r && r.id != null) map.set(String(r.id), r); // local wins
    }
    return Array.from(map.values());
};

export const hydrateFromSupabase = async () => {
    if (!isSupabaseEnabled()) return { ok: false, skipped: true };
    const tables = [SUPABASE_TABLES.SERVICES, SUPABASE_TABLES.EVENTS];
    for (const t of tables) {
        try {
            const { data, error } = await supabase.from(t).select("*").limit(500);
            if (error || !Array.isArray(data)) continue;
            const key = STORAGE_KEYS[t];
            if (!key) continue;
            const local = JSON.parse(localStorage.getItem(key) || "[]");
            const merged = mergeById(Array.isArray(local) ? local : [], data);
            localStorage.setItem(key, JSON.stringify(merged));
        } catch { /* swallow — offline-first */ }
    }
    return { ok: true };
};
